import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { ConnectorStatusBanner } from './ConnectorStatusBanner';
import { LaggingResourcesBanner } from './LaggingResourcesBanner';
import { ConnectorActionButtons } from './ConnectorActionButtons';
import { cronExpressionToPreset } from './cronExpressionToPreset';
import { formatDaysOfWeek } from '@/lib/cron/dayOfWeek';
import { EditableConnectorName } from './EditableConnectorName';
import { PlatformLogo } from '@/components/connectors/library/PlatformLogo';
import { readLastLibraryUrl } from '@/lib/lastLibraryUrl';
import type { Connector } from '@/types/connectors';

interface Props {
  connector: Connector;
  isStopping: boolean;
  isTriggering: boolean;
  onRunNow: () => void;
  onStopRequested: () => void;
  onDelete: () => void;
}

function statusVariant(s?: string) {
  if (s === 'connected') return 'default';
  if (s === 'running')   return 'secondary';
  if (s === 'error')     return 'destructive';
  return 'outline';
}

function scheduleSummary(connector: Connector): string {
  const cfg = connector.config;
  if (!cfg?.schedule_enabled) return 'Not scheduled';
  const preset = cronExpressionToPreset(cfg.schedule_cron ?? null);
  if (!preset) return 'Scheduled';
  // A Daily schedule on a weekday subset names its days, not "daily" (#2191).
  if (preset.frequency === 'daily' && preset.days.length < 7) return `Scheduled ${formatDaysOfWeek(preset.days)}`;
  return `Scheduled ${preset.frequency}`;
}

export function ConnectorHeader({ connector, isStopping, isTriggering, onRunNow, onStopRequested, onDelete }: Props) {
  const back = readLastLibraryUrl();
  const created = connector.created_at ? format(new Date(connector.created_at), 'MMM d, yyyy') : null;
  const meta = [
    connector.connectorType,
    connector.id != null ? `ID ${connector.id}` : null,
    created ? `Created ${created}` : null,
    scheduleSummary(connector),
  ].filter(Boolean) as string[];

  return (
    <header className="space-y-4">
      <Link to={back} className="inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        <span>Connectors</span>
      </Link>

      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-lg border bg-card">
          <PlatformLogo connectorType={connector.connectorType} size={28} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <EditableConnectorName
              configId={connector.id ?? ''}
              connectorType={connector.connectorType}
              name={connector.name}
            />
            {connector.status && (
              <Badge variant={statusVariant(connector.status)} className="text-[11px] font-bold uppercase leading-none tracking-[0.5px]">
                {connector.status}
              </Badge>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            {meta.map((m, i) => (
              <span key={m} className="flex items-center gap-2">
                {i > 0 && <span className="h-[3px] w-[3px] rounded-full bg-muted-foreground/60" aria-hidden />}
                <span className={i <= 1 ? 'select-text font-mono' : undefined}>{m}</span>
              </span>
            ))}
          </div>
        </div>

        <ConnectorActionButtons
          status={connector.latest_execution?.status}
          isStopping={isStopping}
          isTriggering={isTriggering}
          onRunNow={onRunNow}
          onStopRequested={onStopRequested}
          onDelete={onDelete}
        />
      </div>

      <ConnectorStatusBanner latestExecution={connector.latest_execution ?? null} />
      <LaggingResourcesBanner resources={connector.laggingResources} />
    </header>
  );
}
